import { useChatStore } from "../store/useChatStore";
import { useEffect, useRef, useState, Fragment } from "react";
import ChatHeader from "./ChatHeader";
import MessageInput from "./MessageInput";
import MessageSkeleton from "./skeletons/MessageSkeleton";
import { useAuthStore } from "../store/useAuthStore";
import { formatMessageTime } from "../lib/utils";
import FileHistorySidebar from "./FileHistorySidebar";
import { triggerNotification } from "../lib/notify.js";
import AIInsightSidebar from "./chat/AIInsightSidebar";
import {
  Download,
  FileText,
  Image as ImageIcon,
  File as FileGeneric,
  Music,
  Video,
  Sparkles,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Upload,
  Bell,
  Reply,
  MessageCircle,
} from "lucide-react";
import AudioPlayer from "./AudioPlayer";
import { useMessageInsightStore } from "../store/useMessageInsightStore";
import { axiosInstance } from "../lib/axios";

const BASE_URL = import.meta.env.VITE_API_URL
  ? import.meta.env.VITE_API_URL.replace(/\/api$/, "")
  : "http://localhost:5001";

type MessageEndRef = HTMLDivElement | null;

const ChatContainer = ({
  showFileHistory = true,
  hideHeader = false,
  hideComposer = false,
}: {
  showFileHistory?: boolean;
  hideHeader?: boolean;
  hideComposer?: boolean;
}) => {
  const {
    messages,
    getMessages,
    isMessagesLoading,
    selectedUser,
    subscribeToMessages,
    unsubscribeFromMessages,
    uploadingFile,
    reactMessage,
    setReplyingTo,
    activeProjectId,
  } = useChatStore();

  useEffect(() => {
    if (!selectedUser) return;
    axiosInstance.post(`/messages/read/${selectedUser._id}`);
  }, [selectedUser?._id]);

  const { authUser } = useAuthStore();
  const isBusinessDeveloper = authUser?.role === "Business Developer";

  const messageEndRef = useRef<MessageEndRef>(null);
  const [dragActive, setDragActive] = useState(false);
  const [insightMessageId, setInsightMessageId] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      useMessageInsightStore.getState().clearInsights();
    };
  }, [selectedUser?._id]);

  useEffect(() => {
    if (!messages || !authUser) return;
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) return;

    const isIncoming = lastMessage.senderId !== authUser._id;
    const chatNotActive = document.hidden;

    if (isIncoming && chatNotActive && "serviceWorker" in navigator) {
      const title = `New message from ${selectedUser?.fullName}`;
      const body =
        lastMessage.text ||
        (lastMessage.file?.fileName ? `Sent a file: ${lastMessage.file.fileName}` : "");
      triggerNotification(title, body);
    }
  }, [messages, authUser, selectedUser]);

  useEffect(() => {
    if (!selectedUser) return;
    getMessages(selectedUser._id, activeProjectId);
    subscribeToMessages();
    return () => unsubscribeFromMessages();
  }, [selectedUser, activeProjectId, getMessages, subscribeToMessages, unsubscribeFromMessages]);

  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const getFileType = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase() || "";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
    if (["pdf", "doc", "docx", "txt", "xls", "xlsx"].includes(ext)) return "document";
    if (["mp4", "avi", "mov", "mkv"].includes(ext)) return "video";
    if (["mp3", "wav", "aac", "webm"].includes(ext)) return "audio";
    return "file";
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase() || "";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext))
      return <ImageIcon size={16} className="text-[#00AAF1]" />;
    if (["pdf", "doc", "docx", "txt", "xls", "xlsx", "html", "htm"].includes(ext))
      return <FileText size={16} className="text-[#03488A]" />;
    if (["mp4", "avi", "mov", "mkv"].includes(ext))
      return <Video size={16} className="text-sky-500" />;
    if (["mp3", "wav", "aac"].includes(ext))
      return <Music size={16} className="text-violet-500" />;
    return <FileGeneric size={16} className="text-slate-500" />;
  };

  const extractFiles = () => {
    const files: any[] = [];
    messages?.forEach((msg) => {
      if (msg.file && msg.file.fileUrl) {
        const fileName = msg.file.fileName || "Unknown";
        const ext = fileName.split(".").pop()?.toLowerCase() || "";
        let inferredType = "file";
        if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) inferredType = "image";
        else if (["pdf", "doc", "docx", "txt", "xls", "xlsx"].includes(ext))
          inferredType = "document";
        else if (["mp4", "avi", "mov", "mkv"].includes(ext)) inferredType = "video";
        else if (["mp3", "wav", "aac", "webm"].includes(ext)) inferredType = "audio";

        const fileUrl = msg.file.fileUrl.startsWith("/uploads/")
          ? `${BASE_URL}${msg.file.fileUrl}`
          : msg.file.fileUrl;

        files.push({
          type: inferredType,
          fileName,
          fileUrl,
          fileSize: msg.file.fileSize || 0,
          messageId: msg._id,
          senderName: msg.senderId === authUser?._id ? "You" : selectedUser?.fullName,
          timestamp: msg.createdAt,
        });
      }
    });

    return files.sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  };

  if (isMessagesLoading) {
    return (
      <div className="flex-1 flex flex-col overflow-auto">
        {!hideHeader && <ChatHeader />}
        <MessageSkeleton />
        {!hideComposer && <MessageInput />}
      </div>
    );
  }

  if (!selectedUser) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400">
        Select a user to start chatting
      </div>
    );
  }

  if (!authUser) return null;

  const initialOf = (name = "") => name.trim().charAt(0).toUpperCase() || "?";
  const initials2 = (n = "") => n.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";
  const dayLabel = (d?: Date | string) => {
    if (!d) return "Today";
    const dt = new Date(d);
    const today = new Date();
    const y = new Date(); y.setDate(today.getDate() - 1);
    if (dt.toDateString() === today.toDateString()) return "Today";
    if (dt.toDateString() === y.toDateString()) return "Yesterday";
    return dt.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  };

  return (
    <div className="flex-1 flex overflow-hidden">
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {!hideHeader && <ChatHeader />}

        <div
          className={`flex-1 overflow-y-auto p-5 space-y-5 relative transition custom-scrollbar bg-slate-50/50 ${
            dragActive ? "ring-2 ring-inset ring-primary/30" : ""
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
        >
          {(!messages || messages.length === 0) && (
            <div className="h-full flex flex-col items-center justify-center text-center gap-2 text-slate-400">
              <div className="w-14 h-14 rounded-full bg-slate-100 grid place-items-center">
                <MessageCircle size={26} className="text-slate-400" />
              </div>
              <p className="text-[15px] font-semibold text-slate-500">No conversation yet</p>
              <p className="text-[13px]">Send a message below to start the conversation.</p>
            </div>
          )}
          {messages &&
            messages.length > 0 &&
            messages.map((message, idx) => {
              // Anything NOT from the client (selectedUser) is "our side" (the
              // BD — or an admin/BD messaging on the BD's behalf) → render right.
              // This keeps admins' oversight view correct: client bubbles left,
              // BD/on-behalf bubbles right (instead of being mislabeled as the
              // client). For normal 1-1 threads this is identical to before.
              const isSent = selectedUser?._id
                ? String(message.senderId) !== String(selectedUser._id)
                : message.senderId === authUser._id;
              // On-behalf marker: only the BD/admin side ever sees this; clients
              // never do (their own messages carry no actualSenderId).
              const onBehalf = !!message.actualSenderId && isSent;
              const isLast = idx === messages.length - 1;
              const prev = messages[idx - 1];
              const showDivider =
                !prev ||
                new Date(prev.createdAt || 0).toDateString() !==
                  new Date(message.createdAt || 0).toDateString();
              const fileUrl = message.file?.fileUrl?.startsWith("/uploads/")
                ? `${BASE_URL}${message.file.fileUrl}`
                : message.file?.fileUrl;
              // Prefer the real author name (backend resolves it for admin
              // oversight — e.g. the BD's name on on-behalf messages) and only
              // fall back to the two-party assumption for normal threads.
              const name = (message as any).senderName
                || (isSent ? (authUser.fullName || "You") : (selectedUser.fullName || "—"));

              // System notifications (milestone / payment / project events)
              // render as a centered, visually distinct card with a contextual
              // action instead of a chat bubble.
              if (message.messageType === "system") {
                const ev = message.event || {};
                const isMilestone = (ev.kind || "").startsWith("milestone");
                const sysText = String(message.text || "").replace(/^[^\p{L}\p{N}$]+/u, "");
                const sys =
                  ev.kind === "milestone_completed" ? { Icon: CheckCircle2, tile: "bg-green-500/10 text-green-600" }
                  : ev.kind === "milestone_changes_requested" ? { Icon: AlertTriangle, tile: "bg-error-container text-error" }
                  : ev.kind === "milestone_submitted" ? { Icon: Upload, tile: "bg-primary/10 text-primary" }
                  : { Icon: Bell, tile: "bg-primary/10 text-primary" };
                return (
                  <Fragment key={message._id}>
                    {showDivider && (
                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-px bg-outline-variant/50" />
                        <span className="text-[11px] font-semibold text-outline uppercase tracking-wider">{dayLabel(message.createdAt)}</span>
                        <div className="flex-1 h-px bg-outline-variant/50" />
                      </div>
                    )}
                    <div id={`msg-${message._id}`} className="flex justify-center" ref={isLast ? messageEndRef : undefined}>
                      <div className="max-w-md w-full bg-primary-container/15 border border-primary/25 rounded-xl px-4 py-3 shadow-sm">
                        <div className="flex items-start gap-2.5">
                          <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${sys.tile}`}>
                            <sys.Icon size={15} />
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <p className="text-[13px] text-on-surface whitespace-pre-wrap">{sysText}</p>
                            <div className="mt-1.5 flex items-center gap-3">
                              <span className="text-[11px] text-outline">{formatMessageTime(message.createdAt)}</span>
                              {isMilestone && ev.milestoneIndex !== null && ev.milestoneIndex !== undefined && (
                                <button
                                  onClick={() => window.dispatchEvent(new CustomEvent("cre:view-milestone", { detail: { milestoneIndex: ev.milestoneIndex } }))}
                                  className="text-[12px] font-bold text-primary hover:underline"
                                >
                                  View Milestone
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Fragment>
                );
              }

              return (
                <Fragment key={message._id}>
                  {showDivider && (
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-px bg-outline-variant/50" />
                      <span className="text-[11px] font-semibold text-outline uppercase tracking-wider">{dayLabel(message.createdAt)}</span>
                      <div className="flex-1 h-px bg-outline-variant/50" />
                    </div>
                  )}

                  <div
                    id={`msg-${message._id}`}
                    className={`flex items-start gap-3 max-w-2xl group/msg ${isSent ? "ml-auto flex-row-reverse" : ""}`}
                    ref={isLast ? messageEndRef : undefined}
                  >
                    {/* Avatar */}
                    {!isSent && selectedUser.profilePic ? (
                      <img src={selectedUser.profilePic} alt={name} className="w-10 h-10 rounded-full border border-outline-variant shadow-sm shrink-0 object-cover" />
                    ) : (
                      <div className={`w-10 h-10 rounded-full grid place-items-center text-white font-bold text-sm shrink-0 shadow-sm ${isSent ? "bg-primary" : "bg-gradient-to-br from-slate-300 to-slate-400"}`}>
                        {initials2(name)}
                      </div>
                    )}

                    <div className={`space-y-1.5 min-w-0 flex flex-col ${isSent ? "items-end" : "items-start"}`}>
                      {/* Name + time */}
                      <div className={`flex items-center gap-2 ${isSent ? "flex-row-reverse" : ""}`}>
                        <span className="text-[13px] font-semibold text-on-surface">{name}</span>
                        <span className="text-[11px] text-outline">{formatMessageTime(message.createdAt)}</span>
                        {onBehalf && (
                          <span
                            className="text-[10px] font-bold text-amber-700 bg-amber-100 ring-1 ring-amber-200 px-1.5 py-0.5 rounded-full"
                            title="Sent by an admin on the BD's behalf"
                          >
                            via Admin
                          </span>
                        )}
                      </div>

                      {/* Reply quote */}
                      {message.replyTo && message.replyTo.messageId && (
                        <div className="max-w-full border-l-2 border-primary/50 bg-surface-container-low rounded-md px-2 py-1 text-[11px] text-on-surface-variant">
                          <span className="font-semibold text-on-surface">{message.replyTo.senderName || "Reply"}:</span> <span className="line-clamp-1">{message.replyTo.text}</span>
                        </div>
                      )}

                      {/* File / image / audio */}
                      {message.file && (() => {
                        const type = getFileType(message.file.fileName);
                        if (type === "audio") return <div className="w-64 max-w-full"><AudioPlayer src={fileUrl} /></div>;
                        if (type === "image") return <img src={fileUrl} alt={message.file.fileName} className="max-w-[260px] rounded-xl shadow-sm border border-outline-variant/40" />;
                        return (
                          <div className="w-full max-w-sm bg-white border border-outline-variant p-2 rounded-xl flex items-center gap-3 hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-error-container text-error rounded-lg flex items-center justify-center shrink-0">
                              {getFileIcon(message.file.fileName)}
                            </div>
                            <div className="flex-1 overflow-hidden text-left min-w-0">
                              <p className="text-[13px] font-semibold truncate text-on-surface">{message.file.fileName}</p>
                              {message.file.fileSize ? <p className="text-[11px] text-outline">{(message.file.fileSize / 1024 / 1024).toFixed(1)} MB</p> : null}
                            </div>
                            <a
                              href={fileUrl && fileUrl.includes("/uploads/") ? fileUrl : fileUrl?.replace("/upload/", "/upload/fl_attachment/")}
                              download={message.file.fileName}
                              className="p-1 text-outline hover:text-primary transition-colors shrink-0"
                              title={`Download ${message.file.fileName}`}
                            >
                              <Download size={18} />
                            </a>
                          </div>
                        );
                      })()}

                      {/* Text bubble */}
                      {message.text && (
                        <div
                          className={`px-4 py-2.5 text-[14px] leading-relaxed shadow-sm flex items-end gap-1.5 ${
                            isSent
                              ? "bg-primary text-on-primary rounded-tl-xl rounded-bl-xl rounded-br-xl shadow-md"
                              : "bg-white border border-outline-variant/30 text-on-surface rounded-tr-xl rounded-br-xl rounded-bl-xl"
                          }`}
                        >
                          <span className="break-words whitespace-pre-wrap">{message.text}</span>
                          {isSent && (
                            <span className="ml-1 flex items-center shrink-0 mb-0.5">
                              {message.status === "sending" && <Clock size={11} className="opacity-70" />}
                              {message.status === "sent" && <Check size={13} className="opacity-80" />}
                              {message.status === "delivered" && <CheckCheck size={13} className="opacity-80" />}
                              {message.status === "read" && <CheckCheck size={13} className="text-secondary-container" />}
                              {message.status === "failed" && <AlertCircle size={13} className="text-error-container" />}
                            </span>
                          )}
                        </div>
                      )}

                      {/* AI Insight (BD only, on received) */}
                      {!isSent && isBusinessDeveloper && (
                        <button
                          onClick={() => setInsightMessageId(insightMessageId === message._id ? null : message._id)}
                          className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-200 hover:bg-violet-100 transition"
                        >
                          <Sparkles size={11} /> AI Insight
                        </button>
                      )}
                      {insightMessageId === message._id && <AIInsightSidebar messageId={message._id} />}

                      {/* Reactions + hover actions */}
                      <div className={`flex items-center gap-1.5 ${isSent ? "flex-row-reverse" : ""}`}>
                        {message.reactions && message.reactions.length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap">
                            {Object.entries(
                              message.reactions.reduce((acc: Record<string, number>, r) => { acc[r.emoji] = (acc[r.emoji] || 0) + 1; return acc; }, {})
                            ).map(([emoji, count]) => (
                              <button
                                key={emoji}
                                onClick={() => reactMessage(message._id, emoji)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-container-low border border-outline-variant/60 leading-none hover:border-primary transition-colors"
                              >
                                <span className="text-[13px]">{emoji}</span>
                                {count > 1 && <span className="text-[11px] font-semibold text-on-surface-variant">{count}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                        {/* Floating reaction picker (appears on hover) — standard messaging reactions */}
                        <div className="opacity-0 group-hover/msg:opacity-100 transition-opacity flex items-center bg-white border border-outline-variant rounded-full shadow-sm px-1.5 py-1 gap-1">
                          {["👍", "❤️", "😂", "😮", "😢", "🙏"].map((e) => (
                            <button key={e} onClick={() => reactMessage(message._id, e)} title="React" className="w-7 h-7 grid place-items-center rounded-full text-[16px] leading-none hover:bg-surface-container hover:scale-110 transition-all">{e}</button>
                          ))}
                          <span className="w-px h-4 bg-outline-variant/60 mx-0.5" />
                          <button onClick={() => setReplyingTo(message)} title="Reply" className="w-7 h-7 grid place-items-center rounded-full text-outline hover:bg-surface-container hover:text-primary transition-colors"><Reply size={14} /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Fragment>
              );
            })}

          {uploadingFile && (
            <div className="flex items-start gap-3 max-w-2xl ml-auto flex-row-reverse">
              <div className="w-10 h-10 rounded-full bg-primary grid place-items-center text-white font-bold text-sm shrink-0 shadow-sm">
                {initials2(authUser.fullName)}
              </div>
              <div className="bg-primary/10 border border-primary/20 rounded-tl-xl rounded-bl-xl rounded-br-xl px-4 py-2.5 flex items-center gap-2.5 shadow-sm">
                <div className="animate-spin border-2 border-primary border-t-transparent rounded-full size-4" />
                <span className="text-[13px] font-semibold text-primary">Uploading <strong>{uploadingFile.name}</strong>…</span>
              </div>
            </div>
          )}
        </div>

        {!hideComposer && <MessageInput />}
      </div>

      {showFileHistory && <FileHistorySidebar files={extractFiles()} />}
    </div>
  );
};

export default ChatContainer;
